// Mefloquine (Lariam®) is a supply PGD. requiresLariamCard: true — the
// Lariam Patient Alert Card must be supplied and documented. This is
// enforced by the runner's documentation checklist. Weight-banded through
// brands (4 bands). Weekly dosing. Start 10 days before departure (first
// dose 10 days before, second dose 3 days before) to assess tolerability.
// This PGD has the most extensive psychiatric exclusion list in the
// module — every item transcribed exactly.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.MEFLOQUINE,
  displayName: "Mefloquine (Lariam\u00ae)",
  serviceType: "supply",
  minimumAgeYears: 1,
  minimumWeightKg: 5,
  indicationSummary:
    "Malaria prophylaxis for travel to areas with mefloquine-sensitive malaria, as determined by NaTHNaC assessment and current ACMP guidance. Note: mefloquine resistance is present in parts of South-East Asia \u2014 confirm suitability for the specific itinerary. Patient must weigh at least 5 kg and be at least 12 months of age.",

  brands: [
    {
      brandId: "mefloquine-band-5-19",
      brandName: "Lariam\u00ae (5\u201319 kg)",
      minimumAgeYears: 1,
      minimumWeightKg: 5,
      maximumWeightKg: 19,
      route: "supply",
      doseDescription: "\u00bc tablet (62.5 mg) once weekly",
      administrationNotes:
        "Supply 250 mg tablets with a pill cutter. Label: one quarter of a tablet (62.5 mg) once weekly. Take on the same day each week with food and plenty of water. Start 10 days before entering the malaria-endemic area to assess tolerability.",
    },
    {
      brandId: "mefloquine-band-20-30",
      brandName: "Lariam\u00ae (20\u201330 kg)",
      minimumAgeYears: 1,
      minimumWeightKg: 20,
      maximumWeightKg: 30,
      route: "supply",
      doseDescription: "\u00bd tablet (125 mg) once weekly",
      administrationNotes:
        "Supply 250 mg tablets with a pill cutter. Label: half a tablet (125 mg) once weekly. Take on the same day each week with food and plenty of water. Start 10 days before entering the malaria-endemic area to assess tolerability.",
    },
    {
      brandId: "mefloquine-band-31-45",
      brandName: "Lariam\u00ae (31\u201345 kg)",
      minimumAgeYears: 1,
      minimumWeightKg: 31,
      maximumWeightKg: 45,
      route: "supply",
      doseDescription: "\u00be tablet (187.5 mg) once weekly",
      administrationNotes:
        "Supply 250 mg tablets with a pill cutter. Label: three quarters of a tablet (187.5 mg) once weekly. Take on the same day each week with food and plenty of water. Start 10 days before entering the malaria-endemic area to assess tolerability.",
    },
    {
      brandId: "mefloquine-band-over-45",
      brandName: "Lariam\u00ae (over 45 kg)",
      minimumAgeYears: 1,
      minimumWeightKg: 46,
      route: "supply",
      doseDescription: "1 tablet (250 mg) once weekly",
      administrationNotes:
        "Supply 250 mg tablets. Label: one tablet (250 mg) once weekly. Take on the same day each week with food and plenty of water. Start 10 days before entering the malaria-endemic area to assess tolerability.",
    },
  ],

  schedules: [
    {
      scheduleId: "mefloquine-weekly",
      label: "Once weekly \u2014 start 10 days before entering risk area; continue 4 weeks after leaving",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "First dose: 10 days before entering the malaria-endemic area",
        },
        {
          doseNumber: 2,
          label: "Second dose: 3 days before entering the malaria-endemic area (7 days after dose 1)",
        },
      ],
      completionNotes:
        "Continue once weekly on the same day throughout the trip. Continue for 4 weeks (28 days) after leaving the malaria-endemic area. Minimum course: 6 weeks. Maximum course: 12 months. The two doses before departure allow tolerability to be assessed before travel \u2014 if neuropsychiatric symptoms develop during this pre-travel period, stop immediately and seek medical advice before departure. Quantity: pre-travel doses + weekly doses during trip + 4 weekly doses post-travel.",
    },
  ],

  capturesPriorDoseHistory: false,

  supplyGates: [
    "NaTHNaC assessment confirms the destination has malaria risk and mefloquine is appropriate for the resistance pattern of the specific itinerary (note: mefloquine resistance is present in parts of South-East Asia).",
    "Patient weight has been confirmed and is at least 5 kg.",
    "Patient age has been confirmed as at least 12 months.",
    "Departure date and return date are confirmed and the quantity to supply has been calculated: pre-travel weekly doses + weekly doses during trip + 4 weekly doses post-travel.",
  ],

  exclusions: [
    {
      checkId: "mefloquine-ex-weight-age",
      question: "Does the patient weigh less than 5 kg or is the patient under 12 months of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Mefloquine is not indicated for patients weighing less than 5 kg or under 12 months of age. Do not supply.",
      referralMessage: "Refer to the patient's GP or a specialist travel health clinic for advice on malaria prophylaxis.",
    },
    {
      checkId: "mefloquine-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to mefloquine, any related compound (including quinine or quinidine), or any excipient of Lariam\u00ae?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Mefloquine is contraindicated in this patient. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
    {
      checkId: "mefloquine-ex-psychiatric",
      question: "Does the patient have a current or past history of any of the following: depression, generalised anxiety disorder, psychosis, suicidal ideation or attempts, self-endangering behaviour, schizophrenia, or any other psychiatric disorder? Or does the patient have a history of convulsions of any origin or a diagnosed epileptic disorder?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "A current or past history of psychiatric disorder (including depression, anxiety, psychosis, suicidality, or schizophrenia) or a history of convulsions or epileptic disorder is a contraindication to mefloquine. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
    {
      checkId: "mefloquine-ex-conditions",
      question: "Does the patient have any of the following: severe hepatic impairment, a known cardiac conduction disturbance, myasthenia gravis, or is the patient breastfeeding?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Severe hepatic impairment, cardiac conduction disturbance, myasthenia gravis, and breastfeeding are each a contraindication to mefloquine. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
    {
      checkId: "mefloquine-ex-halofantrine",
      question: "Is the patient currently taking halofantrine, or has the patient taken halofantrine within the last 15 weeks?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Halofantrine must not be used during mefloquine prophylaxis or within 15 weeks of the last mefloquine dose due to the risk of fatal QTc prolongation. Do not supply mefloquine.",
      referralMessage: "Refer to the patient's GP for advice on malaria prophylaxis.",
    },
    {
      checkId: "mefloquine-ex-other-conditions",
      question: "Does the patient have a history of blackwater fever, or has the patient been diagnosed with rare hereditary galactose intolerance, Lapp lactase deficiency, or glucose-galactose malabsorption?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "A history of blackwater fever and rare hereditary galactose-related conditions are each a contraindication to mefloquine. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on alternative malaria prophylaxis.",
    },
  ],

  cautions: [
    {
      checkId: "mefloquine-caut-qtc-drugs",
      question:
        "Is the patient taking any QTc-prolonging medicines, including anti-arrhythmics, beta-blockers, calcium-channel blockers, H1 antihistamines, tricyclic antidepressants, phenothiazines, or macrolide antibiotics (erythromycin or clarithromycin)?",
      cautionMessage:
        "QTc-prolonging medicines increase the risk of cardiac arrhythmia when taken with mefloquine. Macrolide antibiotics (erythromycin, clarithromycin) also inhibit CYP3A4 and may raise mefloquine levels. Consider switching to an alternative antimalarial (Malarone\u00ae or doxycycline) if the patient is taking a macrolide antibiotic. For other QTc-prolonging drugs, assess the individual risk before proceeding. Document the clinical decision.",
    },
    {
      checkId: "mefloquine-caut-anticonvulsants",
      question: "Is the patient taking anticonvulsant medicines or any other medicine known to lower the seizure threshold?",
      cautionMessage:
        "Mefloquine may reduce the efficacy of anticonvulsants and increase the risk of seizures in susceptible patients. This is a caution (patients with a diagnosed epileptic disorder or history of convulsions are excluded). Review the patient's anticonvulsant therapy before proceeding. Document the clinical decision.",
    },
    {
      checkId: "mefloquine-caut-doacs",
      question: "Is the patient taking a direct oral anticoagulant (DOAC) such as dabigatran, rivaroxaban, apixaban, or edoxaban?",
      cautionMessage:
        "Mefloquine inhibits CYP3A4 and P-glycoprotein and may increase plasma levels of DOACs, increasing the risk of bleeding. Advise the patient to monitor for signs of bleeding. Notify the patient's GP of the supply.",
    },
    {
      checkId: "mefloquine-caut-neuropsychiatric-warning",
      question:
        "Has the pharmacist counselled the patient on the neuropsychiatric side effects of mefloquine and the importance of stopping immediately if symptoms develop?",
      cautionMessage:
        "Mefloquine can cause neuropsychiatric adverse effects including anxiety, depression, abnormal dreams, confusion, and in rare cases hallucinations or psychosis. These effects can persist for months after stopping the medicine. Advise the patient: if they experience anxiety, depression, confusion, abnormal dreams, or any mental-state change, stop mefloquine immediately and seek medical advice before continuing to take it or continuing travel. Prodromal neuropsychiatric symptoms may precede more serious events. Document that this counselling was given.",
    },
    {
      checkId: "mefloquine-caut-driving-machinery",
      question: "Does the patient drive, operate heavy machinery, pilot an aircraft, or participate in deep-sea diving?",
      cautionMessage:
        "Mefloquine can cause dizziness, loss of balance, and neuropsychiatric effects that may impair the ability to drive or operate machinery. These effects may persist for months after stopping the medicine. Advise the patient to be cautious about driving, operating machinery, piloting, and deep-sea diving, both during the course and for some time afterwards. Document this advice.",
    },
    {
      checkId: "mefloquine-caut-pregnancy",
      question: "Is the patient planning a pregnancy during or shortly after the period of mefloquine use?",
      cautionMessage:
        "Mefloquine may be used in pregnancy where the benefit outweighs the risk and no suitable alternative exists, in consultation with the patient's GP or obstetrician. Supply under this PGD is for non-pregnant patients \u2014 if the patient is pregnant, this is an exclusion. For patients planning pregnancy: mefloquine has a long half-life. Advise the patient to discuss timing of conception with their GP.",
    },
  ],

  requiresICVP: false,
  requiresLariamCard: true,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: false,
  requiresGPNotification: true,
  standardObservationMinutes: 0,
  extendedObservationMinutes: 0,

  patientAdvicePoints: [
    "Take one dose once a week on the same day each week, with food and plenty of water.",
    "Start taking mefloquine 10 days before entering the malaria-endemic area. This allows you to check for side effects before you travel. If you experience any unusual symptoms during this pre-travel period, stop immediately and seek medical advice.",
    "Continue taking mefloquine every week throughout your trip and for 4 weeks after you leave the malaria-endemic area.",
    "You have been given a Lariam\u00ae Patient Alert Card. Carry it with you throughout your treatment and for 3 months after your last dose. Show it to any doctor, dentist, or pharmacist who treats you during this time.",
    "Mefloquine can cause dizziness, loss of balance, anxiety, depression, abnormal dreams, or confusion. If you experience any change in your mental state, stop taking mefloquine immediately and seek medical advice before continuing. These effects can sometimes persist for months after stopping.",
    "Be cautious about driving, operating machinery, piloting aircraft, or deep-sea diving during and for some time after finishing your course of mefloquine.",
    "Continue to use mosquito-bite avoidance measures throughout your trip: apply insect repellent containing DEET to exposed skin, wear long-sleeved clothing and long trousers (especially from dusk to dawn), and sleep under a permethrin-treated bed net.",
    "If you develop a fever or flu-like illness at any time during your trip or within 1 year of returning (especially within the first 3 months), seek urgent medical attention immediately and tell the clinician you have been in a malaria-risk area. Malaria can be fatal if not treated promptly.",
    "No antimalarial gives 100% protection. Using mefloquine together with bite-avoidance measures gives you the best protection available.",
  ],
});
